import { Box, CircularProgress } from '@mui/material'
import { L, Icon } from '../ui/LavDropdown'
import { MbIc, MobileSheet } from './mbBits'
import { OK_BG, OK_BORDER, OK_TEXT, OK_IC, WARN_BG, WARN_BORDER, WARN_TEXT, WARN_IC } from './apdShared'
import {
	buildMoveMessage,
	checkSlot,
	firstName,
	fmtClock,
	fmtFull,
	fmtRange,
	groomerNames,
	notifyPlan,
	timeToMins,
} from './rescheduleShared'

const RAIL_DOT = '#B9B2CF'

/**
 * RescheduleConfirm — the confirm step of the reschedule flow (design
 * AprMoveConfirm): old → new on a route rail, the slot's verdict, and how
 * the client is told. The rescheduled email always goes out when there's an
 * address on file; the text is the one optional channel (the toggle), when
 * the shop can text and the client has a number and hasn't opted out.
 * Desktop renders a centered dialog over the panel; mobile a bottom sheet.
 */
export default function RescheduleConfirm({
	mobile = false,
	from,                 // { date, time }
	fromEmpIds = [],
	to,                   // { date, time, groomer, empIds, check }
	mins,
	petNames,
	clientName,
	client,
	merchant,
	employees = [],
	busyByDate = {},
	notify,               // boolean — the toggle
	setNotify,
	saving = false,
	onBack,
	onConfirm,            // (sent: 'text' | 'email' | 'text+email' | null) => void
	onOpenClient = null,  // (where: 'profile' | 'notifications') => void
}) {
	const first = firstName(clientName)
	const fs = timeToMins(from.time), ts = timeToMins(to.time)
	const check = to.check || checkSlot({ ds: to.date, start: ts, mins, empIds: to.empIds || [], busyByDate, merchant, employees })
	const ok = check.kind === 'open'
	const reassigned = to.groomer && to.groomer !== 'booked'
	const toNames = groomerNames(to.empIds || [], employees, true)
	// One toggle, on by default: whether the client is told. The channel is
	// never picked here — it comes from the client's settings (texts / emails
	// on or off) and the contacts on file; the card only shows what will be used.
	const plan = notifyPlan({ client, merchant })
	const on = !!notify && plan.can
	const msg = buildMoveMessage({ clientName, petNames, merchantName: merchant?.name || 'our shop', date: to.date, time: to.time })
	// Only a text is previewed: the email is composed server-side (send-appointment-email).
	const textGoesOut = on && plan.usable.some((u) => u.kind === 'text')
	const toggle = () => { if (plan.can) setNotify(!notify) }
	const open = (where) => (e) => { e.stopPropagation(); if (onOpenClient) onOpenClient(where) }
	// What's wanted but can't be sent: a missing contact, or (texts) no shop number.
	const missContact = plan.missing.filter((m) => m.reason === 'no-contact').map((m) => m.kind)
	const noTwilio = plan.missing.some((m) => m.reason === 'no-twilio')
	const missWhat = missContact.length === 2 ? 'phone number or email' : missContact[0] === 'text' ? 'phone number' : 'email address'
	const missWhy = plan.wantsText && plan.wantsEmail ? 'Texts and emails are on' : plan.wantsText ? 'Texts are on' : 'Emails are on'
	const missAdd = missContact.length === 2 ? 'Add contact details' : missContact[0] === 'text' ? 'Add phone number' : 'Add email address'
	const missIcon = missContact[0] === 'email' ? 'mail' : 'text'
	const noteIcon = !plan.on ? 'ban' : missContact.length ? missIcon : 'text'
	const noteWhy = !plan.on
		? 'Notifications are off for this client.'
		: missContact.length
			? `${missWhy}, but there’s no ${missWhat} on file.${noTwilio ? ' Texting also isn’t set up for your shop yet.' : ''}`
			: 'Texts are on, but texting isn’t set up for your shop yet (Settings → Messaging).'
	const noteLink = !plan.on ? 'Client settings' : missContact.length ? missAdd : null
	const cta = ok ? 'Move appointment' : 'Move anyway'
	const confirm = () => { if (!saving) onConfirm(on ? plan.sent : null) }

	const rowSx = {
		position: 'relative', display: 'flex', alignItems: 'flex-start', gap: '10px',
		p: mobile ? '12px 0 12px 24px' : '10px 0 10px 22px',
		fontSize: mobile ? '15px' : '13.5px', lineHeight: mobile ? '22px' : '20px', fontVariantNumeric: 'tabular-nums',
	}
	const dotSx = {
		position: 'absolute', left: 0, top: mobile ? 17 : 15, width: mobile ? 12 : 10, height: mobile ? 12 : 10,
		borderRadius: '50%', boxSizing: 'border-box', border: `2px solid ${RAIL_DOT}`, bgcolor: L.surface,
	}
	const railLeft = mobile ? 5 : 4

	const body = (
		<>
			{/* old → new rail (design .apr-fromto) */}
			<Box
				role="group"
				aria-label={`From ${fmtFull(from.date)} at ${fmtClock(fs)} to ${fmtFull(to.date)} at ${fmtClock(ts)}`}
				sx={{
					mt: '14px', bgcolor: L.surface, border: `1px solid ${L.border}`,
					borderRadius: mobile ? '14px' : '12px', p: mobile ? '4px 16px' : '3px 14px',
				}}
			>
				<Box sx={{
					...rowSx, color: L.muted, fontWeight: 600,
					textDecoration: 'line-through', textDecorationColor: RAIL_DOT, textDecorationThickness: '1.5px',
					'&::after': { content: '""', position: 'absolute', left: railLeft, width: 2, bgcolor: L.border, top: mobile ? 31 : 27, bottom: 0 },
				}}>
					<Box component="span" aria-hidden="true" sx={dotSx} />
					<Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '7px' }}>{fmtFull(from.date)}</Box>
					<Box component="span" sx={{ flex: 'none', ml: 'auto', whiteSpace: 'nowrap' }}>{fmtRange(fs, fs + mins)}</Box>
				</Box>
				<Box sx={{
					...rowSx, color: L.text, fontWeight: 750,
					'&::before': { content: '""', position: 'absolute', left: railLeft, width: 2, bgcolor: L.border, top: 0, height: mobile ? 15 : 13 },
				}}>
					<Box component="span" aria-hidden="true" sx={{ ...dotSx, borderColor: L.primary, bgcolor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` }} />
					<Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
						<Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
							{fmtFull(to.date)}
							<Box component="em" sx={{
								fontStyle: 'normal', fontSize: mobile ? '10.5px' : '10px', lineHeight: mobile ? '16px' : '15px', fontWeight: 800,
								letterSpacing: '0.06em', textTransform: 'uppercase', color: L.primaryDeep, bgcolor: L.primarySoft,
								p: '0 6px', borderRadius: '99px',
							}}>
								New
							</Box>
						</Box>
						{reassigned && (
							<Box component="span" sx={{ fontSize: mobile ? '13px' : '12px', lineHeight: mobile ? '18px' : '16px', fontWeight: 600, color: L.muted, mt: '1px', textWrap: 'pretty' }}>
								{groomerNames(fromEmpIds, employees, true)} → <Box component="b" sx={{ color: L.primaryDeep, fontWeight: 750 }}>{toNames}</Box>
							</Box>
						)}
					</Box>
					<Box component="span" sx={{ flex: 'none', ml: 'auto', whiteSpace: 'nowrap' }}>{fmtRange(ts, ts + mins)}</Box>
				</Box>
			</Box>

			{/* verdict bar */}
			<Box sx={{
				display: 'flex', alignItems: 'center', gap: '8px', mt: '10px', borderRadius: mobile ? '12px' : L.rSm,
				p: mobile ? '12px 13px' : '10px 12px', fontSize: mobile ? '13px' : '12.5px', fontWeight: 650,
				border: `1.5px solid ${ok ? OK_BORDER : WARN_BORDER}`, bgcolor: ok ? OK_BG : WARN_BG, color: ok ? OK_TEXT : WARN_TEXT,
				'& svg': { color: ok ? OK_IC : WARN_IC, flex: 'none' },
			}}>
				{ok ? <MbIc name="checkc" size={15} /> : <MbIc name="warn" size={15} />}
				<Box component="span" sx={{ flex: 1, minWidth: 0 }}>
					{ok ? `Open · nothing else booked for ${toNames} then` : check.note}
				</Box>
			</Box>

			{/* telling the client — one toggle; channel + contact come from the client's settings */}
			{plan.can ? (
				<Box
					onClick={toggle}
					sx={{
						display: 'flex', alignItems: 'center', gap: '11px', mt: '12px', p: mobile ? '12px 13px' : '10px 12px',
						border: `1.5px solid ${on ? L.primary : L.border}`, borderRadius: mobile ? '14px' : '12px',
						bgcolor: on ? '#F7F5FC' : '#FFFFFF', cursor: 'pointer', transition: 'border-color .12s, background .12s',
						'&:hover': { borderColor: on ? L.primary : '#D8CEF2' },
					}}
				>
					<Box sx={{
						width: mobile ? 38 : 32, height: mobile ? 38 : 32, flex: 'none', borderRadius: mobile ? '12px' : '10px',
						bgcolor: on ? L.primarySoft : L.surface, color: on ? L.primaryDeep : L.muted,
						display: 'flex', alignItems: 'center', justifyContent: 'center',
					}}>
						<MbIc name={plan.preview === 'email' ? 'mail' : 'text'} size={15} />
					</Box>
					<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
						<Box component="strong" sx={{ fontSize: mobile ? '15px' : '13.5px', fontWeight: 750, color: on ? L.primaryDeep : L.text }}>
							Send {first} the new time
						</Box>
						{on ? plan.usable.map((u) => (
							<ContactLine key={u.kind} mobile={mobile} icon={u.icon} onClick={open('notifications')} ariaLabel={`Open ${first}’s notification settings`}>
								{u.to}
							</ContactLine>
						)) : (
							<Box component="span" sx={{ fontSize: mobile ? '12.5px' : '12px', fontWeight: 600, color: L.muted }}>
								You’ll let {first} know yourself
							</Box>
						)}
						{on && missContact.length > 0 && (
							<ContactLine mobile={mobile} icon={missIcon} missing onClick={open('profile')}>
								No {missWhat} on file · add one
							</ContactLine>
						)}
						{on && noTwilio && (
							<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', mt: '2px', fontSize: mobile ? '13px' : '12px', fontWeight: 650, color: WARN_TEXT }}>
								<MbIc name="text" size={13} /><span>No text · texting isn’t set up for your shop</span>
							</Box>
						)}
					</Box>
					<Switch on={on} mobile={mobile} ariaLabel={`Send ${first} the new time`} onClick={(e) => { e.stopPropagation(); toggle() }} />
				</Box>
			) : (
				<Box sx={{
					display: 'flex', alignItems: 'flex-start', gap: '11px', mt: '12px', p: mobile ? '12px 13px' : '10px 12px',
					border: `1.5px dashed ${L.border}`, borderRadius: mobile ? '14px' : '12px', bgcolor: L.surface,
				}}>
					<Box sx={{
						width: mobile ? 38 : 32, height: mobile ? 38 : 32, flex: 'none', borderRadius: mobile ? '12px' : '10px',
						bgcolor: '#FFFFFF', color: L.muted, display: 'flex', alignItems: 'center', justifyContent: 'center',
					}}>
						<MbIc name={noteIcon} size={15} />
					</Box>
					<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
						<Box component="strong" sx={{ fontSize: mobile ? '15px' : '13.5px', fontWeight: 750, color: L.text }}>{first} won’t be notified</Box>
						<Box component="span" sx={{ fontSize: mobile ? '12.5px' : '12px', fontWeight: 600, color: L.muted, textWrap: 'pretty' }}>
							{noteWhy}
						</Box>
						{onOpenClient && noteLink && (
							<Box
								component="button"
								type="button"
								onClick={open(plan.on ? 'profile' : 'notifications')}
								sx={{
									display: 'inline-flex', alignItems: 'center', gap: '1px', alignSelf: 'flex-start', mt: '5px', p: 0,
									minHeight: mobile ? 30 : 'auto', border: 'none', bgcolor: 'transparent', fontFamily: 'inherit',
									fontSize: mobile ? '13px' : '12px', fontWeight: 750, color: L.primaryDeep, cursor: 'pointer',
									'&:hover > span': { textDecoration: 'underline' },
									'& svg': { transform: 'rotate(-90deg)' },
								}}
							>
								<span>{noteLink}</span><Icon name="chevron" size={12} />
							</Box>
						)}
					</Box>
				</Box>
			)}

			{textGoesOut && (
				<Box sx={{
					mt: '8px', bgcolor: L.surface, borderRadius: mobile ? '13px' : '12px', p: mobile ? '11px 13px' : '10px 12px',
					fontSize: mobile ? '13px' : '12.5px', fontWeight: 550, lineHeight: 1.5, color: L.text, textWrap: 'pretty',
				}}>
					<Box component="span" sx={{ display: 'block', fontSize: '11px', fontWeight: 750, textTransform: 'uppercase', letterSpacing: '0.05em', color: L.muted, mb: '5px' }}>
						Text preview · sends when you confirm
					</Box>
					{msg}
				</Box>
			)}
		</>
	)

	if (mobile) {
		return (
			<MobileSheet open onClose={saving ? () => {} : onBack}>
				<Box sx={{ overflowY: 'auto', minHeight: 0, px: '2px', pb: '8px', textAlign: 'left' }}>
					<Box component="strong" sx={{ display: 'block', fontSize: '19px', fontWeight: 800, letterSpacing: '-0.01em', color: L.text, p: '2px 2px 0' }}>
						Move appointment?
					</Box>
					<Box component="span" sx={{ display: 'block', fontSize: '13px', fontWeight: 600, color: L.muted, mt: '2px', px: '2px' }}>
						{petNames} · {mins} mins
					</Box>
					{body}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mt: '20px' }}>
						<Box
							component="button"
							type="button"
							disabled={saving}
							onClick={confirm}
							sx={{
								width: '100%', height: 52, flex: 'none', border: 'none', borderRadius: '14px', bgcolor: L.primary, color: '#FFFFFF',
								fontSize: '15.5px', fontWeight: 750, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
								boxShadow: '0 3px 12px rgba(124, 107, 217, 0.32)', fontFamily: L.font, cursor: 'pointer',
								'&:active': { bgcolor: L.primaryDeep }, '&:disabled': { opacity: 0.7, boxShadow: 'none', cursor: 'default' },
							}}
						>
							{saving ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null}
							<span>{saving ? 'Moving…' : cta}</span>
						</Box>
						<Box
							component="button"
							type="button"
							disabled={saving}
							onClick={onBack}
							sx={{
								width: '100%', height: 52, flex: 'none', borderRadius: '14px', border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF',
								color: L.muted, fontSize: '15px', fontWeight: 700, fontFamily: L.font, cursor: 'pointer',
								'&:active': { color: L.text, borderColor: L.muted }, '&:disabled': { opacity: 0.6 },
							}}
						>
							Back
						</Box>
					</Box>
				</Box>
			</MobileSheet>
		)
	}

	return (
		<Box
			onClick={saving ? undefined : onBack}
			sx={{
				position: 'absolute', inset: 0, zIndex: 50, bgcolor: 'rgba(46, 42, 64, 0.32)',
				display: 'flex', alignItems: 'center', justifyContent: 'center', p: '24px', fontFamily: L.font,
			}}
		>
			<Box
				onClick={(e) => e.stopPropagation()}
				sx={{
					width: '100%', maxWidth: 372, maxHeight: '100%', overflowY: 'auto', bgcolor: '#FFFFFF', borderRadius: '16px',
					p: '18px 18px 16px', boxShadow: '0 18px 44px rgba(46, 42, 64, 0.28)', color: L.text,
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
					<Box>
						<Box component="strong" sx={{ display: 'block', fontSize: '15.5px', fontWeight: 750 }}>Move appointment?</Box>
						<Box component="p" sx={{ m: '7px 0 0', fontSize: '12.5px', lineHeight: 1.5, color: L.muted, fontWeight: 600 }}>{petNames} · {mins} mins</Box>
					</Box>
					<Box
						component="button"
						type="button"
						aria-label="Back"
						onClick={onBack}
						disabled={saving}
						sx={{
							width: 28, height: 28, flex: 'none', borderRadius: '50%', border: `1px solid ${L.border}`, bgcolor: L.surface,
							color: L.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit',
							'&:hover': { color: L.text, borderColor: L.muted },
						}}
					>
						<MbIc name="x" size={13} sw={2} />
					</Box>
				</Box>
				{body}
				<Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', mt: '15px' }}>
					<Box
						component="button"
						type="button"
						disabled={saving}
						onClick={onBack}
						sx={{
							height: 38, px: '14px', borderRadius: L.rSm, border: `1.5px solid ${L.border}`, bgcolor: 'transparent',
							fontSize: '13px', fontWeight: 650, color: L.muted, fontFamily: 'inherit', cursor: 'pointer',
							'&:hover': { color: L.text, borderColor: L.muted }, '&:disabled': { opacity: 0.6, cursor: 'default' },
						}}
					>
						Back
					</Box>
					<Box
						component="button"
						type="button"
						disabled={saving}
						onClick={confirm}
						sx={{
							display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', height: 38, px: '14px',
							borderRadius: L.rSm, border: 'none', bgcolor: L.primary, color: L.onPrimary, fontSize: '13px', fontWeight: 700,
							boxShadow: `0 2px 8px ${L.primarySoft}`, whiteSpace: 'nowrap', fontFamily: 'inherit', cursor: 'pointer',
							'&:hover': { bgcolor: L.primaryDeep },
							'&:disabled': { opacity: 0.65, cursor: 'default', boxShadow: 'none', bgcolor: L.primary },
						}}
					>
						{saving ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : null}
						<span>{saving ? 'Moving…' : cta}</span>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

/* the contact line(s): which channel + contact will be used; tap → the client's settings */
function ContactLine({ icon, children, onClick, ariaLabel, missing = false, mobile }) {
	return (
		<Box
			component="button"
			type="button"
			aria-label={ariaLabel}
			onClick={onClick}
			sx={{
				display: 'inline-flex', alignItems: 'center', gap: '5px', alignSelf: 'flex-start', maxWidth: '100%',
				minHeight: mobile ? 34 : 22, m: mobile ? '0 0 0 -6px' : '0 0 0 -5px', p: mobile ? '0 6px' : '0 5px',
				border: 'none', borderRadius: '6px', bgcolor: 'transparent', fontFamily: 'inherit',
				fontSize: mobile ? '13px' : '12px', fontWeight: 650, color: missing ? WARN_TEXT : L.text,
				fontVariantNumeric: 'tabular-nums', cursor: 'pointer', textAlign: 'left',
				'& svg': { flex: 'none' },
				'&:hover': { color: L.primaryDeep, bgcolor: L.primarySoft },
				'&:hover .apr-go': { color: L.primaryDeep },
			}}
		>
			<MbIc name={icon} size={13} />
			<Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</Box>
			<Box component="span" className="apr-go" sx={{ display: 'flex', transform: 'rotate(-90deg)', color: L.muted }}>
				<Icon name="chevron" size={12} />
			</Box>
		</Box>
	)
}

function Switch({ on, onClick, ariaLabel, mobile }) {
	const w = mobile ? 50 : 40, h = mobile ? 29 : 24, k = mobile ? 23 : 18
	return (
		<Box
			component="button"
			type="button"
			role="switch"
			aria-checked={on}
			aria-label={ariaLabel}
			onClick={onClick}
			sx={{
				display: 'block', width: w, height: h, flex: 'none', borderRadius: '99px', bgcolor: on ? L.primary : L.border,
				position: 'relative', transition: 'background .15s', border: 'none', p: 0, m: 0, cursor: 'pointer',
				'&::after': {
					content: '""', position: 'absolute', top: 3, left: on ? w - k - 3 : 3, width: k, height: k, borderRadius: '50%',
					bgcolor: '#FFFFFF', transition: 'left .15s', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.25)',
				},
				'&:focus-visible': { outline: 'none', boxShadow: `0 0 0 3px ${L.primarySoft}` },
			}}
		/>
	)
}
